/**
 * post-V9 Section 0 Iter 3 — compact stores for variable-length
 * polyline-family primitives: Spline, LWPOLYLINE.
 *
 * Variable-length child arrays use the offset/length (CSR-like) layout:
 * each variable-length array gets a flat data buffer plus a parallel
 * Uint32Array offset column. To read entity i's vertices we read
 * offsets[i] and offsets[i+1] and slice the data buffer.
 */

import type {
  DrawingPoint3D,
  LwpolylineEntity,
  SplineEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import { type SharedStringTable } from "./sharedStringTable";
import {
  packPolylineVertices,
  unpackPolylineVertices,
} from "./compactVertexPacking";

/* ── SPLINE ── */

const SPLINE_FLAGS_FLOATS = 5;
const FLAG_CLOSED = 1;
const FLAG_PERIODIC = 2;
const FLAG_RATIONAL = 4;
const FLAG_PLANAR = 8;

export interface CompactSplineStore {
  count: number;
  base: BaseColumns;
  tolerances: Float64Array;
  flags: Uint8Array;
  degrees: Uint8Array;

  fitPointData: Float32Array;
  fitOffsets: Uint32Array;
  controlPointData: Float32Array;
  controlOffsets: Uint32Array;
  knotData: Float64Array;
  knotOffsets: Uint32Array;
  weightData: Float64Array;
  weightOffsets: Uint32Array;

  startTangents: Map<number, DrawingPoint3D>;
  endTangents: Map<number, DrawingPoint3D>;
  // SL-15 — the SPLINE normal (DXF 210/220/230), sparse (present only for a
  // planar spline in a tilted plane). Stored verbatim (the bridge only sets a
  // NON-default extrusion), absent ⇒ the WCS (0,0,1) default.
  extrusions: Map<number, DrawingPoint3D>;
}

const SPLINE_INITIAL_DATA = 4096;

export function createCompactSplineStore(
  initialCapacity = 256,
): CompactSplineStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    tolerances: new Float64Array(cap * SPLINE_FLAGS_FLOATS),
    flags: new Uint8Array(cap),
    degrees: new Uint8Array(cap),
    fitPointData: new Float32Array(SPLINE_INITIAL_DATA),
    fitOffsets: new Uint32Array(cap + 1),
    controlPointData: new Float32Array(SPLINE_INITIAL_DATA),
    controlOffsets: new Uint32Array(cap + 1),
    knotData: new Float64Array(SPLINE_INITIAL_DATA),
    knotOffsets: new Uint32Array(cap + 1),
    weightData: new Float64Array(SPLINE_INITIAL_DATA),
    weightOffsets: new Uint32Array(cap + 1),
    startTangents: new Map(),
    endTangents: new Map(),
    extrusions: new Map(),
  };
}

function ensureSplineEntityCapacity(
  store: CompactSplineStore,
  needed: number,
): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const nt = new Float64Array(newCap * SPLINE_FLAGS_FLOATS);
  nt.set(store.tolerances);
  const nf = new Uint8Array(newCap);
  nf.set(store.flags);
  const nd = new Uint8Array(newCap);
  nd.set(store.degrees);
  const nfo = new Uint32Array(newCap + 1);
  nfo.set(store.fitOffsets);
  const nco = new Uint32Array(newCap + 1);
  nco.set(store.controlOffsets);
  const nko = new Uint32Array(newCap + 1);
  nko.set(store.knotOffsets);
  const nwo = new Uint32Array(newCap + 1);
  nwo.set(store.weightOffsets);
  store.tolerances = nt;
  store.flags = nf;
  store.degrees = nd;
  store.fitOffsets = nfo;
  store.controlOffsets = nco;
  store.knotOffsets = nko;
  store.weightOffsets = nwo;
}

function ensureFloatBufferCapacity(
  buf: Float32Array,
  needed: number,
): Float32Array {
  if (needed <= buf.length) return buf;
  let cap = Math.max(buf.length, 1);
  while (cap < needed) cap *= 2;
  const nu = new Float32Array(cap);
  nu.set(buf);
  return nu;
}

function ensureF64BufferCapacity(
  buf: Float64Array,
  needed: number,
): Float64Array {
  if (needed <= buf.length) return buf;
  let cap = Math.max(buf.length, 1);
  while (cap < needed) cap *= 2;
  const nu = new Float64Array(cap);
  nu.set(buf);
  return nu;
}

export function appendSpline(
  store: CompactSplineStore,
  table: SharedStringTable,
  entity: SplineEntity,
): number {
  const index = store.count;
  ensureSplineEntityCapacity(store, index + 1);
  store.count++;

  store.degrees[index] = Math.min(entity.degree, 0xff);
  let f = 0;
  if (entity.closed) f |= FLAG_CLOSED;
  if (entity.periodic) f |= FLAG_PERIODIC;
  if (entity.rational) f |= FLAG_RATIONAL;
  if (entity.planar) f |= FLAG_PLANAR;
  store.flags[index] = f;
  const t = index * SPLINE_FLAGS_FLOATS;
  store.tolerances[t + 0] = entity.knotTolerance;
  store.tolerances[t + 1] = entity.controlPointTolerance;
  store.tolerances[t + 2] = entity.fitTolerance;

  writeBase(store.base, table, index, entity);

  const fitStart = index === 0 ? 0 : store.fitOffsets[index]!;
  const fitFloats = entity.fitPoints.length * 3;
  store.fitPointData = ensureFloatBufferCapacity(
    store.fitPointData,
    fitStart + fitFloats,
  );
  for (let i = 0; i < entity.fitPoints.length; i++) {
    const p = entity.fitPoints[i]!;
    store.fitPointData[fitStart + i * 3 + 0] = p.x;
    store.fitPointData[fitStart + i * 3 + 1] = p.y;
    store.fitPointData[fitStart + i * 3 + 2] = p.z;
  }
  store.fitOffsets[index] = fitStart;
  store.fitOffsets[index + 1] = fitStart + fitFloats;

  const ctrlStart = index === 0 ? 0 : store.controlOffsets[index]!;
  const ctrlFloats = entity.controlPoints.length * 3;
  store.controlPointData = ensureFloatBufferCapacity(
    store.controlPointData,
    ctrlStart + ctrlFloats,
  );
  for (let i = 0; i < entity.controlPoints.length; i++) {
    const p = entity.controlPoints[i]!;
    store.controlPointData[ctrlStart + i * 3 + 0] = p.x;
    store.controlPointData[ctrlStart + i * 3 + 1] = p.y;
    store.controlPointData[ctrlStart + i * 3 + 2] = p.z;
  }
  store.controlOffsets[index] = ctrlStart;
  store.controlOffsets[index + 1] = ctrlStart + ctrlFloats;

  const knotStart = index === 0 ? 0 : store.knotOffsets[index]!;
  store.knotData = ensureF64BufferCapacity(
    store.knotData,
    knotStart + entity.knots.length,
  );
  for (let i = 0; i < entity.knots.length; i++) {
    store.knotData[knotStart + i] = entity.knots[i]!;
  }
  store.knotOffsets[index] = knotStart;
  store.knotOffsets[index + 1] = knotStart + entity.knots.length;

  const weightStart = index === 0 ? 0 : store.weightOffsets[index]!;
  store.weightData = ensureF64BufferCapacity(
    store.weightData,
    weightStart + entity.weights.length,
  );
  for (let i = 0; i < entity.weights.length; i++) {
    store.weightData[weightStart + i] = entity.weights[i]!;
  }
  store.weightOffsets[index] = weightStart;
  store.weightOffsets[index + 1] = weightStart + entity.weights.length;

  if (entity.startTangent !== undefined) {
    store.startTangents.set(index, { ...entity.startTangent });
  }
  if (entity.endTangent !== undefined) {
    store.endTangents.set(index, { ...entity.endTangent });
  }
  // SL-15 — the DXF 210/220/230 normal (append-only fresh index, like the
  // tangents above; stored only when present, absent ⇒ WCS default).
  if (entity.extrusion !== undefined) {
    store.extrusions.set(index, { ...entity.extrusion });
  }
  return index;
}

export function splineFromCompact(
  store: CompactSplineStore,
  table: SharedStringTable,
  index: number,
): SplineEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `splineFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const t = index * SPLINE_FLAGS_FLOATS;
  const flags = store.flags[index] ?? 0;

  const fitStart = store.fitOffsets[index]!;
  const fitEnd = store.fitOffsets[index + 1]!;
  const fitPoints: DrawingPoint3D[] = [];
  for (let i = fitStart; i < fitEnd; i += 3) {
    fitPoints.push({
      x: store.fitPointData[i + 0]!,
      y: store.fitPointData[i + 1]!,
      z: store.fitPointData[i + 2]!,
    });
  }

  const ctrlStart = store.controlOffsets[index]!;
  const ctrlEnd = store.controlOffsets[index + 1]!;
  const controlPoints: DrawingPoint3D[] = [];
  for (let i = ctrlStart; i < ctrlEnd; i += 3) {
    controlPoints.push({
      x: store.controlPointData[i + 0]!,
      y: store.controlPointData[i + 1]!,
      z: store.controlPointData[i + 2]!,
    });
  }

  const knotStart = store.knotOffsets[index]!;
  const knotEnd = store.knotOffsets[index + 1]!;
  const knots: number[] = [];
  for (let i = knotStart; i < knotEnd; i++) knots.push(store.knotData[i]!);

  const weightStart = store.weightOffsets[index]!;
  const weightEnd = store.weightOffsets[index + 1]!;
  const weights: number[] = [];
  for (let i = weightStart; i < weightEnd; i++) {
    weights.push(store.weightData[i]!);
  }

  const result: SplineEntity = {
    ...readBase(store.base, table, index),
    type: "Spline",
    degree: store.degrees[index] ?? 3,
    closed: (flags & FLAG_CLOSED) !== 0,
    periodic: (flags & FLAG_PERIODIC) !== 0,
    rational: (flags & FLAG_RATIONAL) !== 0,
    planar: (flags & FLAG_PLANAR) !== 0,
    fitPoints,
    controlPoints,
    knots,
    weights,
    knotTolerance: store.tolerances[t + 0]!,
    controlPointTolerance: store.tolerances[t + 1]!,
    fitTolerance: store.tolerances[t + 2]!,
  };
  const sTan = store.startTangents.get(index);
  if (sTan !== undefined) result.startTangent = sTan;
  const eTan = store.endTangents.get(index);
  if (eTan !== undefined) result.endTangent = eTan;
  // SL-15 — restore the DXF 210/220/230 normal when one was stored.
  const ext = store.extrusions.get(index);
  if (ext !== undefined) result.extrusion = ext;
  return result;
}

export function estimateSplineBytesPerEntity(
  store: CompactSplineStore,
): number {
  const fixed =
    SPLINE_FLAGS_FLOATS * 8 + 2 + BASE_BYTES_PER_ENTITY + 16;
  if (store.count === 0) return fixed;
  const fitBytes = store.fitOffsets[store.count]! * 4;
  const ctrlBytes = store.controlOffsets[store.count]! * 4;
  const knotBytes = store.knotOffsets[store.count]! * 8;
  const weightBytes = store.weightOffsets[store.count]! * 8;
  const variable =
    (fitBytes + ctrlBytes + knotBytes + weightBytes) / store.count;
  return fixed + variable;
}

/* ── LWPOLYLINE ── */

const LWPOLY_FIXED_FLOATS = 2;
const LWPOLY_VERTEX_FLOATS = 5;
const LW_FLAG_CLOSED = 1;

export interface CompactLwpolylineStore {
  count: number;
  base: BaseColumns;
  fixed: Float32Array;
  flags: Uint8Array;
  vertexData: Float32Array;
  vertexOffsets: Uint32Array;
  vertexIdData: Map<number, number[]>;
}

export function createCompactLwpolylineStore(
  initialCapacity = 256,
): CompactLwpolylineStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    fixed: new Float32Array(cap * 3),
    flags: new Uint8Array(cap),
    vertexData: new Float32Array(2048),
    vertexOffsets: new Uint32Array(cap + 1),
    vertexIdData: new Map(),
  };
}

function ensureLwpolyEntityCapacity(
  store: CompactLwpolylineStore,
  needed: number,
): void {
  if (needed <= store.base.capacity) return;
  const newCap = growBaseColumns(store.base, needed);
  const nf = new Float32Array(newCap * 3);
  nf.set(store.fixed);
  const nfl = new Uint8Array(newCap);
  nfl.set(store.flags);
  const no = new Uint32Array(newCap + 1);
  no.set(store.vertexOffsets);
  store.fixed = nf;
  store.flags = nfl;
  store.vertexOffsets = no;
}

export function appendLwpolyline(
  store: CompactLwpolylineStore,
  table: SharedStringTable,
  entity: LwpolylineEntity,
): number {
  const index = store.count;
  ensureLwpolyEntityCapacity(store, index + 1);
  store.count++;

  const fx = index * 3;
  store.fixed[fx + 0] = entity.constantWidth;
  store.fixed[fx + 1] = entity.elevation;
  store.fixed[fx + 2] = entity.thickness;
  store.flags[index] = entity.closed ? LW_FLAG_CLOSED : 0;
  writeBase(store.base, table, index, entity);

  // Shared CSR vertex packing (one-truth helper; same layout as REVCLOUD).
  packPolylineVertices(store, index, entity.vertices);
  return index;
}

export function lwpolylineFromCompact(
  store: CompactLwpolylineStore,
  table: SharedStringTable,
  index: number,
): LwpolylineEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `lwpolylineFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const fx = index * 3;
  const flags = store.flags[index] ?? 0;
  const vertices = unpackPolylineVertices(store, index);
  return {
    ...readBase(store.base, table, index),
    type: "LWPOLYLINE",
    vertices: vertices as LwpolylineEntity["vertices"],
    closed: (flags & LW_FLAG_CLOSED) !== 0,
    constantWidth: store.fixed[fx + 0]!,
    elevation: store.fixed[fx + 1]!,
    thickness: store.fixed[fx + 2]!,
  };
}

export function estimateLwpolylineBytesPerEntity(
  store: CompactLwpolylineStore,
): number {
  const fixed =
    LWPOLY_FIXED_FLOATS * 4 + 4 + 1 + 8 + BASE_BYTES_PER_ENTITY;
  if (store.count === 0) return fixed;
  const totalVertices = store.vertexOffsets[store.count]!;
  const variable = (totalVertices * LWPOLY_VERTEX_FLOATS * 4) / store.count;
  return fixed + variable;
}
